import type { QueryClient, QueryKey } from "@tanstack/react-query";
import type { CalendarPart, RealtimeEnvelope } from "@/types/realtime.type";
import type { RealtimeHandler } from "../../api/realtimeHandlers";
import { postThreadsKey } from "../comments/commentApi";
import { guestMonthsKey } from "../guest/useGuestCalendar";
import { reconcileAllPlans, reconcilePlan, removeSlotFromCache } from "./calendarCache";
import { conceptVersionsKey } from "./useConceptVersions";
import { customMediaKey } from "./useCustomMedia";
import { liveCommentsKey } from "./useLiveComments";
import { briefReviewKey, planBriefsKey, slotBriefKey } from "./useProductionBrief";
import { distributionKey, upcomingKey } from "./useScheduling";

/**
 * Content calendar: which cached reads each realtime hint touches.
 *
 * Hints arrive in bursts (a planned month's ideas land a few seconds apart, a
 * send moves a dozen posts), so they are coalesced: the keys pile up for
 * COALESCE_MS after the last hint, never longer than MAX_WAIT_MS after the
 * first, then refresh once. A month goes through its write gate
 * (`reconcilePlan`): while this tab has a write in flight on it, the refresh is
 * held and the last write to settle does it, so a teammate's change never lands
 * on top of an optimistic one.
 */

const COALESCE_MS = 250;
const MAX_WAIT_MS = 1_000;

type Batch = {
  qc: QueryClient;
  plans: Set<string>;
  allPlans: boolean;
  keys: Map<string, QueryKey>;
  startedAt: number;
  timer?: ReturnType<typeof setTimeout>;
};

let batch: Batch | null = null;

function queue(qc: QueryClient, work: { plans?: string[]; allPlans?: boolean; keys?: QueryKey[] }) {
  const now = Date.now();
  batch ??= { qc, plans: new Set(), allPlans: false, keys: new Map(), startedAt: now };
  for (const planId of work.plans ?? []) batch.plans.add(planId);
  if (work.allPlans) batch.allPlans = true;
  for (const key of work.keys ?? []) batch.keys.set(JSON.stringify(key), key);
  clearTimeout(batch.timer);
  const wait = Math.max(0, Math.min(COALESCE_MS, batch.startedAt + MAX_WAIT_MS - now));
  batch.timer = setTimeout(flush, wait);
}

function flush() {
  const current = batch;
  batch = null;
  if (!current) return;
  const { qc } = current;
  if (current.allPlans) reconcileAllPlans(qc);
  else for (const planId of current.plans) reconcilePlan(qc, planId);
  for (const queryKey of current.keys.values()) void qc.invalidateQueries({ queryKey });
}

const plansListKey = (brandId: string): QueryKey => ["calendarPlans", brandId];

/** A guest's reads of one month (the client portal, pointed at the guest routes). */
function guestMonthKeys(qc: QueryClient, brandId: string, planId: string): QueryKey[] {
  const source = `guest:${brandId}:${planId}`;
  return qc
    .getQueryCache()
    .findAll({ queryKey: ["clientPortal"] })
    .filter((query) => query.queryKey[2] === source)
    .map((query) => query.queryKey);
}

/** The per-post reads the parts name. No posts named means every post's. */
function partKeys(
  brandId: string,
  planId: string,
  slotIds: string[],
  parts: CalendarPart[],
): QueryKey[] {
  const keys: QueryKey[] = [];
  const has = (part: CalendarPart) => parts.includes(part);
  const each = (key: (slotId: string) => QueryKey, all: QueryKey) => {
    if (slotIds.length === 0) keys.push(all);
    else for (const slotId of slotIds) keys.push(key(slotId));
  };

  if (has("concept") || has("review")) each(conceptVersionsKey, ["conceptVersions"]);
  if (has("brief")) {
    each(slotBriefKey, ["slotBrief"]);
    keys.push(planBriefsKey(planId), ["briefRevisions"]);
  }
  if (has("review")) {
    keys.push(briefReviewKey(planId), planBriefsKey(planId), ["calendarNotifyState", planId]);
  }
  if (has("comments")) each(postThreadsKey, ["postThreads"]);
  if (has("files") || has("publishing") || has("live")) each(distributionKey, ["distribution"]);
  // The Schedule view lists posts by day with their publishing state.
  if (has("post") || has("files") || has("publishing")) keys.push(upcomingKey(brandId));
  if (has("live")) {
    each(liveCommentsKey, ["liveComments"]);
    each((slotId) => ["livePostHistory", slotId], ["livePostHistory"]);
  }
  return keys;
}

export const CALENDAR_REALTIME: RealtimeHandler = {
  apply(qc, event: RealtimeEnvelope) {
    const { brandId } = event;
    switch (event.type) {
      case "calendar.slots.changed": {
        const { planId, slotIds, action, parts } = event.data;
        // Straight out of the cache, so the card goes now, not after a refetch.
        if (action === "removed") for (const slotId of slotIds) removeSlotFromCache(qc, planId, slotId);
        // The month carries everything about a post but its threads and live comments.
        const month = parts.some((part) => part !== "comments" && part !== "live");
        queue(qc, {
          plans: month ? [planId] : [],
          keys: [
            ...(month ? [plansListKey(brandId)] : []),
            ...partKeys(brandId, planId, slotIds, parts),
            ...guestMonthKeys(qc, brandId, planId),
            ...(parts.includes("review") ? [guestMonthsKey(brandId)] : []),
          ],
        });
        return;
      }
      case "calendar.plan.changed": {
        const { planId, action } = event.data;
        queue(qc, {
          plans: [planId],
          keys: [
            plansListKey(brandId),
            ...(action === "replanned" ? [planBriefsKey(planId), briefReviewKey(planId)] : []),
            ...guestMonthKeys(qc, brandId, planId),
            guestMonthsKey(brandId),
          ],
        });
        return;
      }
      case "calendar.settings.changed": {
        const { fields } = event.data;
        const pillars = fields.includes("pillars");
        queue(qc, {
          // A renamed pillar shows on the posts tagged with it.
          allPlans: pillars,
          keys: [
            ["calendarSettings", brandId],
            ["calendarSettingsMeta", brandId],
            plansListKey(brandId),
            ...(pillars ? [["calendarPillars", brandId]] : []),
          ],
        });
        return;
      }
      case "calendar.media.changed":
        queue(qc, { keys: event.data.mediaIds.map((mediaId) => customMediaKey(brandId, mediaId)) });
        return;
    }
  },

  resyncKeys: (brandId) => [
    plansListKey(brandId),
    ["calendarPlan"],
    ["planBriefs"],
    ["slotBrief"],
    ["briefRevisions"],
    ["briefReview"],
    ["conceptVersions"],
    ["postThreads"],
    ["distribution"],
    upcomingKey(brandId),
    ["liveComments"],
    ["livePostHistory"],
    ["calendarNotifyState"],
    ["calendarSettings", brandId],
    ["calendarSettingsMeta", brandId],
    ["calendarPillars", brandId],
    ["content-calendar", brandId, "custom-media"],
    guestMonthsKey(brandId),
    ["clientPortal"],
  ],
};
